import type { FullParseSubmissionContext } from "@/api/docParsing.service";
import type {
  SubmissionField,
  SubmissionGroup,
  SubmissionItem,
  SubmissionSection,
  SubmissionSectionId,
} from "./originalSubmissionSections";

export type FieldDefinition = { label: string; fullLabel: string };

function define(label: string, fullLabel: string = label): FieldDefinition {
  return { label, fullLabel };
}

export const SECTION_TITLES: Record<SubmissionSectionId, string> = {
  about: "About the Organisation",
  clients: "Clients",
  team: "Team",
  matters: "Matter Highlights",
  template: "Template-specific fields",
  metadata: "Submission Metadata",
};

export const SECTION_ORDER: SubmissionSectionId[] = [
  "about",
  "clients",
  "team",
  "matters",
  "template",
  "metadata",
];

export const GROUP_TITLES = {
  headsOfTeam: "Head(s) of team",
  publishableClients: "Publishable clients",
  activeKeyClients: "Active key clients (last 12 months)",
  leadingIndividuals: "Leading individuals",
  nextGeneration: "Next generation partners",
  risingStars: "Rising stars",
  arrivalsAndDepartures: "Arrivals and departures",
  summaries: "Summaries",
  publishableMatters: "Publishable matters",
  nonPublishableMatters: "Non-publishable matters",
  arbitrators: "Arbitrators",
  barristers: "Barristers",
  interviewContacts: "Interview contacts",
};

export const ABOUT_FIELDS = {
  lawyerName: define("Lawyer name", "Lawyer Name"),
  firmName: define("Firm name", "Firm Name"),
  department: define("Team / department", "Team/Department Name"),
  partners: define("Partners", "Number of partners"),
  nonPartners: define("Non-partners", "Number of non-partners"),
  members: define("Members in submission", "List of members included in submission"),
  practiceDifferentiator: define(
    "What sets the practice apart",
    "Practice description (what sets your practice apart)",
  ),
  initiatives: define("Initiatives and innovation"),
  seniorClerk: define("Senior clerk"),
  leadClerk: define("Lead clerk"),
  otherClerks: define("Other key clerks"),
  clerkingPractice: define(
    "Clerking and practice management",
    "Clerking/practice management description",
  ),
};

export const HEAD_OF_TEAM_FIELDS = {
  name: define("Name", "Head of team name"),
  location: define("Location", "Head of team location"),
};

export const CLIENT_FIELDS = {
  referees: define("Client referees"),
  client: define("Client", "Active key client"),
  newClient: define("New client", "New client (yes/no)"),
};

export const LEADING_FIELDS = {
  name: define("Name", "Leading individual name"),
  location: define("Location"),
  previouslyRanked: define("Previously ranked", "Ranked in previous edition"),
  supportingInformation: define("Supporting information"),
};

export const NEXT_GENERATION_FIELDS = {
  name: define("Name", "Next generation partner name"),
  location: define("Location"),
  supportingInformation: define("Supporting information"),
};

export const RISING_STAR_FIELDS = {
  name: define("Name", "Rising star or associate name"),
  location: define("Location"),
  supportingInformation: define("Supporting information"),
};

export const ARRIVAL_FIELDS = {
  name: define("Name", "Arrival or departure name"),
  role: define("Position or role", "Position/role"),
  direction: define("Joined / departed / promoted", "Joined/departed/promoted"),
  firm: define("Joined from / destination", "Joined from/destination"),
  date: define("Month / year", "Month/year"),
};

export const SUMMARY_FIELDS = {
  summary: define("Summary", "Publishable matter summary"),
};

export const MATTER_FIELDS = {
  client: define("Client", "Client name"),
  industry: define("Industry sector"),
  description: define("Description", "Matter description"),
  significance: define("Case significance"),
  dealValue: define("Deal value"),
  crossBorder: define("Cross-border", "Cross-border flag"),
  jurisdiction: define("Jurisdiction"),
  leadPartners: define("Lead partners", "Lead partner(s)"),
  leadPartnerOffice: define(
    "Lead partner office and practice area",
    "Lead partner(s) office and practice area",
  ),
  teamMembers: define("Other key team members"),
  teamMemberOffice: define(
    "Team member office and practice area",
    "Other key team members office and practice area",
  ),
  otherFirms: define("Other firms advising", "Other firms advising (firm name, role details)"),
  partyAdvised: define("Party advised", "Advising party name"),
  startDate: define("Start date", "Start date"),
  endDate: define("End date", "End date"),
  otherInformation: define("Other information", "Other information about the matter"),
  currentStatus: define("Current status"),
};

export const LEGACY_MATTER_ITEM_FIELDS = [
  MATTER_FIELDS.client,
  MATTER_FIELDS.industry,
  MATTER_FIELDS.description,
  MATTER_FIELDS.dealValue,
  MATTER_FIELDS.leadPartners,
  MATTER_FIELDS.teamMembers,
];

export const LEGACY_MATTER_NOT_CAPTURED = [
  MATTER_FIELDS.significance,
  MATTER_FIELDS.crossBorder,
  MATTER_FIELDS.jurisdiction,
  MATTER_FIELDS.leadPartnerOffice,
  MATTER_FIELDS.teamMemberOffice,
  MATTER_FIELDS.otherFirms,
  MATTER_FIELDS.partyAdvised,
  MATTER_FIELDS.startDate,
  MATTER_FIELDS.endDate,
  MATTER_FIELDS.otherInformation,
  MATTER_FIELDS.currentStatus,
];

export const ARBITRATOR_FIELDS = {
  name: define("Name", "Arbitrator name"),
  callYear: define("Call year"),
  silkYear: define("Silk year"),
  previouslyRanked: define("Previously ranked", "Ranked in previous edition"),
  careerSummary: define("Career summary"),
  caseHistory: define("Case history"),
};

export const BARRISTER_FIELDS = {
  name: define("Name", "Barrister name"),
  callYear: define("Year of call"),
  silkYear: define("Silk year"),
  otherTenancies: define("Other tenancies"),
  careerSummary: define("Career summary"),
  caseHistory: define("Case history"),
  caseSummaries: define("Publishable case summaries"),
  clients: define("Publishable clients"),
};

export const ARBITRATOR_NOT_CAPTURED = Object.values(ARBITRATOR_FIELDS);

export const LEGACY_BARRISTER_NOT_CAPTURED = [
  BARRISTER_FIELDS.otherTenancies,
  BARRISTER_FIELDS.careerSummary,
  BARRISTER_FIELDS.caseHistory,
  BARRISTER_FIELDS.caseSummaries,
  BARRISTER_FIELDS.clients,
];

export const METADATA_FIELDS = {
  submitted: define("Date of submission", "Date of submission (from parser)"),
  edition: define("Edition"),
  region: define("Region"),
  practiceArea: define("Practice area"),
};

export const INTERVIEW_CONTACT_FIELDS = {
  name: define("Name", "Interview contact name"),
  jobTitle: define("Job title"),
  email: define("Email"),
  phone: define("Phone"),
};

export const MAX_SUMMARIES = 3;
export const MAX_MATTERS_PER_KIND = 20;

const TEMPLATE_SECTION_TYPES = ["arbitration", "bar"];

export function hasTemplateSection(templateType: string | null): boolean {
  return templateType !== null && TEMPLATE_SECTION_TYPES.includes(templateType);
}

/** The text a raw value shows as, or null when there is nothing to show. Never HTML. */
function displayText(raw: unknown): string | null {
  if (typeof raw === "string") return raw.trim() === "" ? null : raw;
  if (typeof raw === "number") return String(raw);
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  if (Array.isArray(raw)) return joinText(raw.map(displayText));
  return null;
}

function joinText(parts: (string | null)[]): string | null {
  const present = parts.filter((part): part is string => part !== null);
  return present.length === 0 ? null : present.join("; ");
}

export function fieldFrom(definition: FieldDefinition, raw: unknown): SubmissionField {
  const text = displayText(raw);
  if (text === null) return { ...definition, kind: "notProvided" };
  return { ...definition, kind: "value", value: text };
}

export function notCapturedField(definition: FieldDefinition): SubmissionField {
  return { ...definition, kind: "notCaptured" };
}

export function itemFrom(pairs: [FieldDefinition, unknown][]): SubmissionItem {
  return { fields: pairs.map(([definition, raw]) => fieldFrom(definition, raw)) };
}

export function groupOf(title: string, items: SubmissionItem[]): SubmissionGroup {
  return { title, items };
}

export function sectionOf(
  id: SubmissionSectionId,
  fields: SubmissionField[],
  groups: SubmissionGroup[],
  notCapturedItemFields: FieldDefinition[] = [],
): SubmissionSection {
  return {
    id,
    title: SECTION_TITLES[id],
    fields,
    groups,
    notCapturedItemFields: notCapturedItemFields.map(definition => definition.label),
  };
}

export function arbitratorsGroup(): SubmissionGroup {
  return groupOf(GROUP_TITLES.arbitrators, []);
}

export function templateSectionOf(
  group: SubmissionGroup,
  notCaptured: FieldDefinition[],
): SubmissionSection {
  return sectionOf("template", [], [group], notCaptured);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatSubmissionDate(parsedAt: string | null): string | null {
  if (parsedAt === null) return null;
  const date = new Date(parsedAt);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export function metadataFields(
  submissionContext: FullParseSubmissionContext | null,
  parsedAt: string | null,
): SubmissionField[] {
  return [
    fieldFrom(METADATA_FIELDS.submitted, formatSubmissionDate(parsedAt)),
    fieldFrom(METADATA_FIELDS.edition, submissionContext?.edition),
    fieldFrom(METADATA_FIELDS.region, submissionContext?.regionName),
    fieldFrom(METADATA_FIELDS.practiceArea, submissionContext?.practiceAreaName),
  ];
}

export function orderSections(
  sections: Partial<Record<SubmissionSectionId, SubmissionSection>>,
): SubmissionSection[] {
  return SECTION_ORDER.map(id => sections[id]).filter(
    (found): found is SubmissionSection => found !== undefined,
  );
}
